// client/src/pages/clientPortal/PortalMyPropertiesPage.js — a signed-in visitor's (or
// client's) property search: new matches for their saved searches, the searches
// themselves (including ones their agent set up) and their favourites. Matches are found
// by the office's daily run; nothing is emailed or texted automatically.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useClientPortalApiClient } from '../../hooks/useClientPortalApiClient';
import { filtersToQuery } from '../../utils/properties';

const API = '/api/client-portal/properties';

const PortalMyPropertiesPage = () => {
    const api = useClientPortalApiClient();
    const [searches, setSearches] = useState([]);
    const [fresh, setFresh] = useState([]);
    const [favs, setFavs] = useState([]);
    const [open, setOpen] = useState(null);
    const [matches, setMatches] = useState([]);
    const [error, setError] = useState('');

    const load = useCallback(() => {
        Promise.all([api.get(`${API}/saved-searches`), api.get(`${API}/new-matches`), api.get(`${API}/favorites`)])
            .then(([s, n, f]) => { setSearches(s); setFresh(n); setFavs(f); })
            .catch((e) => setError(e.message));
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const showMatches = async (id) => {
        if (open === id) { setOpen(null); return; }
        setOpen(id);
        try { setMatches(await api.get(`${API}/saved-searches/${id}/matches`)); } catch (e) { setError(e.message); }
    };
    const remove = async (id) => {
        if (!window.confirm('Delete this saved search?')) return;
        try { await api.del(`${API}/saved-searches/${id}`); load(); } catch (e) { setError(e.message); }
    };
    const toggleAlerts = async (s) => {
        try { await api.patch(`${API}/saved-searches/${s.saved_search_id}`, { alerts_enabled: !s.alerts_enabled }); load(); } catch (e) { setError(e.message); }
    };
    const unfav = async (key) => {
        try { await api.del(`${API}/favorites/${encodeURIComponent(key)}`); load(); } catch (e) { setError(e.message); }
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-2xl font-bold text-slate-900">My Properties</h1>
                <Link to="/properties" className="rounded-md bg-slate-900 px-4 py-2 font-semibold text-white hover:bg-slate-900">Search properties</Link>
            </div>
            {error && <p className="text-red-700" role="alert">{error}</p>}

            <section aria-labelledby="new-h">
                <h2 id="new-h" className="mb-2 text-lg font-semibold text-slate-900">New matches</h2>
                {fresh.length === 0
                    ? <p className="rounded-lg bg-white p-4 text-slate-700">No new matches since you last looked. We check every day.</p>
                    : (
                        <ul className="divide-y divide-slate-100 rounded-lg bg-white shadow">
                            {fresh.map((m) => (
                                <li key={m.alert_id} className="px-4 py-3">
                                    <Link to={m.path} className="font-medium text-indigo-800 underline">{m.summary}</Link>
                                    <span className="block text-xs text-slate-600">For “{m.search_title}” · {new Date(m.queued_at).toLocaleDateString()}</span>
                                </li>
                            ))}
                        </ul>
                    )}
            </section>

            <section aria-labelledby="ss-h">
                <h2 id="ss-h" className="mb-2 text-lg font-semibold text-slate-900">Saved searches</h2>
                {searches.length === 0
                    ? <p className="rounded-lg bg-white p-4 text-slate-700">None yet. Run a search on the <Link className="underline" to="/properties">Properties</Link> page and choose “Save this search”.</p>
                    : (
                        <ul className="space-y-3">
                            {searches.map((s) => (
                                <li key={s.saved_search_id} className="rounded-lg bg-white p-4 shadow">
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div>
                                            <p className="font-semibold text-slate-900">{s.title}</p>
                                            <p className="text-xs text-slate-600">{s.from_agent ? 'Set up by your agent · ' : ''}{s.new_matches ? `${s.new_matches} new` : 'No new matches'}</p>
                                        </div>
                                        <div className="flex flex-wrap gap-2 text-sm">
                                            <Link className="rounded border border-slate-400 px-3 py-1 text-slate-800" to={`/properties?${filtersToQuery(s.criteria || {})}`}>Run search</Link>
                                            <button type="button" className="rounded border border-slate-400 px-3 py-1 text-slate-800" aria-expanded={open === s.saved_search_id} onClick={() => showMatches(s.saved_search_id)}>Matches</button>
                                            <button type="button" className="rounded border border-slate-400 px-3 py-1 text-slate-800" aria-pressed={!!s.alerts_enabled} onClick={() => toggleAlerts(s)}>{s.alerts_enabled ? 'Matching on' : 'Matching off'}</button>
                                            <button type="button" className="rounded border border-red-700 px-3 py-1 text-red-800" onClick={() => remove(s.saved_search_id)}>Delete</button>
                                        </div>
                                    </div>
                                    {open === s.saved_search_id && (
                                        <ul className="mt-3 divide-y divide-slate-100 border-t border-slate-200">
                                            {matches.length === 0 && <li className="py-2 text-sm text-slate-700">No matches yet.</li>}
                                            {matches.map((m) => (
                                                <li key={m.alert_id} className="py-2 text-sm">
                                                    <Link to={m.path} className="text-indigo-800 underline">{m.summary}</Link>{m.is_new ? <span className="ml-2 rounded bg-emerald-100 px-1 text-xs text-emerald-900">new</span> : null}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
            </section>

            <section aria-labelledby="fav-h">
                <h2 id="fav-h" className="mb-2 text-lg font-semibold text-slate-900">Favourites</h2>
                {favs.length === 0
                    ? <p className="rounded-lg bg-white p-4 text-slate-700">Tap “Save to favourites” on any property to keep it here.</p>
                    : (
                        <ul className="divide-y divide-slate-100 rounded-lg bg-white shadow">
                            {favs.map((f) => (
                                <li key={f.listing_key} className="flex items-center justify-between gap-3 px-4 py-3">
                                    <Link to={f.path} className="text-indigo-800 underline">{f.summary || f.listing_key}</Link>
                                    <button type="button" className="text-sm text-red-800 underline" onClick={() => unfav(f.listing_key)}>Remove</button>
                                </li>
                            ))}
                        </ul>
                    )}
            </section>
        </div>
    );
};

export default PortalMyPropertiesPage;
